/**
 * Design tokens — the single source of truth for the visual system.
 *
 * Nothing in the UI should hardcode a colour, radius, duration or spacing
 * value. Everything flows from this file. The tokens are consumed in two ways:
 *
 *   1. As CSS custom properties, emitted at runtime by `applyTheme()` and read
 *      by Tailwind utilities (see `tailwind.config.js`) and raw CSS.
 *   2. As typed values, imported directly into TS when a computed value is
 *      needed (e.g. a transition string passed to an inline style).
 *
 * Keeping both consumers driven by one object is what lets the whole
 * application be re-skinned from a single place.
 */

/** Colour palette. Pure black canvas, white ink, monochrome throughout. */
export const palette = {
  /** Application canvas — pure black. */
  black: "#000000",
  /** Panel elevation one step above the canvas. */
  elevated: "#0a0a0a",
  /** Elevation for popovers / modals sitting above panels. */
  overlay: "#111111",
  /** Hover wash for interactive surfaces. */
  hover: "#161616",
  /** Pressed / active wash. */
  active: "#1f1f1f",

  /** Primary ink. */
  white: "#ffffff",
  /** Near-white for large text blocks — softer than pure white. */
  ink: "#fafafa",
  /** Secondary text. */
  gray: "#8a8a8a",
  /** Tertiary text / disabled. */
  grayDim: "#5a5a5a",
  /** Faintest text — timestamps, hints. */
  grayFaint: "#3d3d3d",

  /** Hairline borders — deliberately very subtle. */
  border: "rgba(255, 255, 255, 0.08)",
  /** Slightly stronger border for focused / emphasised edges. */
  borderStrong: "rgba(255, 255, 255, 0.14)",
  /** Focus ring — white, low presence, never a colour. */
  focus: "rgba(255, 255, 255, 0.55)",

  /** Semantic status inks. Kept desaturated so the UI stays monochrome;
   *  these appear only in small, meaningful moments (log levels, indicators). */
  positive: "#e5e5e5",
  warning: "#c9c9c9",
  danger: "#f5f5f5",
} as const;

/** Semantic colour roles. Components reference *these*, never the palette. */
export const colors = {
  "bg-base": palette.black,
  "bg-elevated": palette.elevated,
  "bg-overlay": palette.overlay,
  "bg-hover": palette.hover,
  "bg-active": palette.active,

  "text-primary": palette.ink,
  "text-secondary": palette.gray,
  "text-tertiary": palette.grayDim,
  "text-faint": palette.grayFaint,

  border: palette.border,
  "border-strong": palette.borderStrong,
  focus: palette.focus,

  accent: palette.white,
  "accent-muted": palette.gray,
} as const;

/** Spacing scale (px). Based on a 4px rhythm. */
export const spacing = {
  0: "0px",
  px: "1px",
  0.5: "2px",
  1: "4px",
  1.5: "6px",
  2: "8px",
  3: "12px",
  4: "16px",
  5: "20px",
  6: "24px",
  8: "32px",
  10: "40px",
  12: "48px",
  16: "64px",
  20: "80px",
  24: "96px",
} as const;

/** Border radii. Tight and precise — no bubbles. */
export const radius = {
  none: "0px",
  sm: "3px",
  md: "5px",
  lg: "8px",
  full: "9999px",
} as const;

/** Typography. Geist for the interface, Geist Mono for numerals and metadata. */
export const typography = {
  fontFamily: {
    sans: '"Geist Sans", ui-sans-serif, -apple-system, "Segoe UI", Roboto, sans-serif',
    mono: '"Geist Mono", ui-monospace, "SF Mono", "Cascadia Code", monospace',
  },
  fontSize: {
    "2xs": "10px",
    xs: "11px",
    sm: "12px",
    base: "13px",
    md: "14px",
    lg: "16px",
    xl: "20px",
    "2xl": "26px",
    "3xl": "34px",
  },
  fontWeight: {
    normal: "400",
    medium: "500",
    semibold: "600",
  },
  lineHeight: {
    tight: "1.2",
    snug: "1.4",
    normal: "1.55",
  },
  letterSpacing: {
    tight: "-0.01em",
    normal: "0em",
    wide: "0.02em",
    /** Uppercase labels — section headers, status bar. */
    caps: "0.08em",
  },
} as const;

/** Shadows. Minimal by design — depth comes from borders and elevation, not glow. */
export const shadow = {
  none: "none",
  /** Barely-there lift for popovers. */
  overlay: "0 8px 24px rgba(0, 0, 0, 0.6)",
  /** Modal drop. */
  modal: "0 16px 48px rgba(0, 0, 0, 0.7)",
} as const;

/** Motion. Fast, restrained, functional. Nothing bounces. */
export const motion = {
  duration: {
    instant: "80ms",
    fast: "120ms",
    base: "160ms",
    slow: "240ms",
  },
  easing: {
    /** Standard ease for most UI transitions. */
    standard: "cubic-bezier(0.4, 0.0, 0.2, 1)",
    /** Entering elements. */
    enter: "cubic-bezier(0.0, 0.0, 0.2, 1)",
    /** Leaving elements. */
    exit: "cubic-bezier(0.4, 0.0, 1, 1)",
  },
} as const;

/** Fixed layout dimensions used across the shell. */
export const layout = {
  sidebarWidth: "224px",
  sidebarCollapsedWidth: "56px",
  toolbarHeight: "44px",
  statusBarHeight: "24px",
  inputBarMinHeight: "56px",
  contentMaxWidth: "1200px",
} as const;

/** Z-index scale — named so stacking order is never guessed. */
export const zIndex = {
  base: 0,
  sticky: 10,
  toolbar: 20,
  sidebar: 30,
  overlay: 40,
  modal: 50,
  toast: 60,
} as const;

/** The complete, immutable theme object. */
export const tokens = {
  palette,
  colors,
  spacing,
  radius,
  typography,
  shadow,
  motion,
  layout,
  zIndex,
} as const;

export type Tokens = typeof tokens;
export type ColorRole = keyof typeof colors;
